import { METERED_DOWNLOAD_CONFIRM_BYTES } from '@/protocol/limits';
import type { Hex } from '@/store/ids';
import type { WorkerClient } from '@/transport/workerClient';
import { isMeteredOrLowData } from '@/media/network';

/**
 * Fetching private media.
 *
 * Section 06: "Private downloads require current authorization through a live
 * message/story reference. A 60-second scoped CDN grant binds media ID, variant,
 * and allowed method." So every download starts by asking for a fresh grant
 * through the live message it is attached to, and no URL is ever cached beyond
 * its expiry — a URL that outlived its grant would simply start failing, and a
 * client that reused one would be relying on the edge not to enforce the
 * revocation it is required to enforce.
 */

export interface DownloadRequest {
  worker: WorkerClient;
  conversationId: Hex;
  messageId: string;
  mediaId: Hex;
  variant: string;
}

/**
 * A grant, and when it stops being usable.
 *
 * Held only in memory and only for the life of one component. Nothing here is
 * written to the local store: Section 06 permits previously issued grants to
 * remain usable for their 60-second lifetime, which is a statement about the
 * edge's behaviour rather than an invitation to persist one.
 */
export interface Grant {
  url: string;
  expiresAt: number;
  etag: string;
}

export async function requestGrant(request: DownloadRequest): Promise<Grant> {
  return request.worker.call('get-download-grant', {
    conversationId: request.conversationId,
    messageId: request.messageId as Hex,
    mediaId: request.mediaId,
    variant: request.variant,
  });
}

/**
 * Whether Section 14's metered-connection confirmation applies.
 *
 * "Confirm before downloading a file above 20 MiB on a metered connection."
 * Both halves are required: a large file on Wi-Fi downloads without a prompt,
 * and a small file on a metered link does too. Prompting for either alone would
 * train the user to dismiss the dialog.
 */
export function needsMeteredConfirmation(sizeBytes: number): boolean {
  return sizeBytes > METERED_DOWNLOAD_CONFIRM_BYTES && isMeteredOrLowData();
}

/**
 * Downloads media to a blob URL the caller is responsible for revoking.
 *
 * The bytes are fetched rather than being handed to the browser as a navigation
 * so that the grant's short lifetime is respected in one place, and so that a
 * failure is a caught error rather than a blank tab.
 */
export async function fetchMedia(request: DownloadRequest): Promise<{ url: string; blob: Blob }> {
  const grant = await requestGrant(request);
  const response = await fetch(grant.url, {
    method: 'GET',
    // The grant points at the CDN, not at this origin's API. Sending cookies
    // there would leak the session to a third party.
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error(`download failed with ${String(response.status)}`);
  }
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), blob };
}

/**
 * Saves a downloaded blob under its display filename.
 *
 * Section 06: "File name is display metadata, never an executable path." The
 * name is therefore stripped of anything that could address a directory before
 * it reaches the download attribute — the browser would do this too, but a
 * client that relies on that is relying on a behaviour it has not checked.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const safe = filename.replace(/[\\/]/g, '_').replace(/^\.+/, '_') || 'download';
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = safe;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next turn of the event loop: revoking synchronously races
  // the browser's own read of the URL in some engines.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}
